/**
 * Agent history page — full-page view of every conversation turn the agent
 * has handled in this browser. Sources from the persisted ai-agent store
 * (localStorage) so it survives refreshes but is per-browser, not global.
 *
 * Filters: free-text search across user prompts + answer/proposal content;
 * type chips (answer | proposal | error) to narrow.
 *
 * Click any user message to re-send it (re-runs the same prompt with the
 * current settings — model, max-rounds, etc.).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles,
  Search,
  Trash2,
  RotateCcw,
  CheckCircle2,
  AlertCircle,
  Send,
  Filter,
  History,
  RefreshCcw,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardSub, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PanelSkeleton, Skeleton } from "@/components/ui/skeleton";
import {
  useAiAgent,
  type AgentMessage,
  type ServerConversation,
} from "@/stores/ai-agent";
import { MarkdownView } from "@/components/app/markdown-view";
import { cn } from "@/lib/cn";
import { useUiAutoAnimate } from "@/components/ui/auto-animate";

type Filter = "all" | "answer" | "proposal" | "error";

export function AgentHistoryPage() {
  const animatedRootRef = useUiAutoAnimate();

  const messages = useAiAgent((s) => s.messages);
  const send = useAiAgent((s) => s.send);
  const clear = useAiAgent((s) => s.clear);
  const setOpen = useAiAgent((s) => s.setOpen);
  const conversationId = useAiAgent((s) => s.conversationId);
  const listConversations = useAiAgent((s) => s.listConversations);
  const loadConversation = useAiAgent((s) => s.loadConversation);
  const deleteConversation = useAiAgent((s) => s.deleteConversation);

  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<Filter>("all");

  // Server-side conversation archive — fetched once on mount, refreshable
  // via the button. Failures fall back to an empty list so the rest of
  // the page still works (e.g. when not logged in to the shim).
  const [serverConvs, setServerConvs] = useState<ServerConversation[]>([]);
  const [serverLoading, setServerLoading] = useState(true);
  const isInitialLoading =
    serverLoading && messages.length === 0 && serverConvs.length === 0;

  const refreshServer = useCallback(
    () =>
      listConversations()
        .then((list) => setServerConvs(list))
        .finally(() => setServerLoading(false)),
    [listConversations],
  );

  useEffect(() => {
    void refreshServer();
    // Refresh when conversationId changes (e.g. after sending the first
    // message of a new thread, the row appears in the archive).
  }, [conversationId, refreshServer]);

  // Pair each user prompt with the agent's reply (if any) so the timeline
  // reads as a sequence of exchanges rather than two parallel streams.
  // We walk top-down: a "user" message starts a turn, the next non-user
  // message closes it.
  const turns = useMemo(() => {
    // Build a Map: for each index i, the first non-user message at index > i.
    // One backward scan: O(n) instead of O(n²) from .find() inside a loop.
    const nextNonUser = new Map<number, AgentMessage>();
    let last: AgentMessage | null = null;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (last !== null) nextNonUser.set(i, last);
      if (messages[i].kind !== "user") last = messages[i];
    }

    const out: {
      user: AgentMessage;
      reply: AgentMessage | null;
      idx: number;
    }[] = [];
    for (let i = 0; i < messages.length; i++) {
      const m = messages[i];
      if (m.kind !== "user") continue;
      out.push({ user: m, reply: nextNonUser.get(i) ?? null, idx: i });
    }
    // Newest first — matches the audit-log convention.
    return out.reverse();
  }, [messages]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return turns.filter((t) => {
      // Filter chip
      if (filter !== "all" && t.reply?.kind !== filter) return false;
      if (!q) return true;
      // Free-text — match on user prompt, answer text, or proposal tool name.
      if (t.user.kind === "user" && t.user.text.toLowerCase().includes(q))
        return true;
      if (t.reply?.kind === "answer" && t.reply.text.toLowerCase().includes(q))
        return true;
      if (
        t.reply?.kind === "proposal" &&
        t.reply.proposal.tool.toLowerCase().includes(q)
      )
        return true;
      if (t.reply?.kind === "error" && t.reply.text.toLowerCase().includes(q))
        return true;
      return false;
    });
  }, [turns, filter, query]);

  if (isInitialLoading) {
    return (
      <div ref={animatedRootRef} className="flex flex-col gap-6">
        <div className="flex items-center gap-2 text-xs text-fg-mute">
          <Skeleton className="h-3 w-20" />
          <span className="opacity-40">/</span>
          <Skeleton className="h-3 w-24" />
        </div>

        <div className="flex items-end justify-between gap-6">
          <div className="flex max-w-[680px] flex-1 flex-col gap-2">
            <Skeleton className="h-10 w-48" />
            <Skeleton className="h-4 w-[92%]" />
            <Skeleton className="h-4 w-[84%]" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-9 w-28 rounded-lg" />
            <Skeleton className="h-9 w-24 rounded-lg" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <PanelSkeleton />
          <PanelSkeleton />
          <PanelSkeleton />
          <PanelSkeleton />
        </div>

        <PanelSkeleton className="h-[220px]" />
        <PanelSkeleton className="h-[88px]" />
        <PanelSkeleton className="h-[260px]" />
      </div>
    );
  }

  return (
    <div ref={animatedRootRef} className="flex flex-col gap-6">
      {/* Crumbs */}
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <Link to="/" className="text-fg-dim hover:text-fg">
          Operations
        </Link>
        <span className="opacity-40">/</span>
        <span className="text-fg">Agent history</span>
      </div>

      {/* Hero */}
      <div className="flex items-end justify-between gap-6">
        <div className="min-w-0">
          <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">
            Agent history
          </h1>
          <p className="mt-1.5 max-w-[680px] text-sm text-fg-dim">
            Every conversation turn handled in this browser. Lives in
            localStorage — clearing browser data wipes it. Click any prompt to
            re-send.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>
            <Sparkles size={13} />
            Open agent
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              if (confirm("Clear the entire transcript? This can't be undone."))
                clear();
            }}
            disabled={messages.length === 0}
          >
            <Trash2 size={13} />
            Clear all
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KpiTile label="Total turns" value={turns.length} />
        <KpiTile
          label="Answers"
          value={turns.filter((t) => t.reply?.kind === "answer").length}
        />
        <KpiTile
          label="Proposals"
          value={turns.filter((t) => t.reply?.kind === "proposal").length}
        />
        <KpiTile
          label="Errors"
          value={turns.filter((t) => t.reply?.kind === "error").length}
        />
      </div>

      <ServerArchiveCard
        serverConvs={serverConvs}
        conversationId={conversationId}
        loadConversation={loadConversation}
        deleteConversation={deleteConversation}
        serverLoading={serverLoading}
        refreshServer={refreshServer}
        setOpen={setOpen}
      />

      {/* Search + filters */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-3">
          <div className="flex h-9 min-w-[280px] flex-1 items-center gap-2 rounded-md border border-line bg-surface px-3">
            <Search size={13} className="text-fg-mute" />
            <input
              type="text"
              aria-label="Filter by prompt, answer, tool name"
              placeholder="Filter by prompt, answer, tool name…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="flex-1 bg-transparent text-sm outline-none"
            />
            {query ? (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="text-[11px] text-fg-mute hover:text-fg"
              >
                clear
              </button>
            ) : null}
          </div>
          <div className="flex items-center gap-1.5">
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">
              <Filter size={10} className="mr-1 inline" />
              Show
            </span>
            {(["all", "answer", "proposal", "error"] as const).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                className={cn(
                  "rounded-full border px-2.5 py-1 text-[11px] font-semibold capitalize transition-colors",
                  filter === f
                    ? "border-violet/40 bg-aurora-soft text-violet"
                    : "border-line bg-surface text-fg-mute hover:bg-surface-2 hover:text-fg",
                )}
              >
                {f}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Timeline */}
      {filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-14 text-center">
            <Sparkles size={20} className="text-violet" />
            <CardTitle>
              {messages.length === 0
                ? "No agent history yet"
                : "Nothing matches your filter"}
            </CardTitle>
            <CardSub>
              {messages.length === 0
                ? "Open the agent (⌘K) and ask something — your turns will show here."
                : "Try widening the filter or clearing the search."}
            </CardSub>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-3">
          {filtered.map((t) => (
            <TurnRow
              key={t.idx}
              userMsg={t.user}
              reply={t.reply}
              onRerun={() => void send((t.user as { text: string }).text)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ServerArchiveCard({
  serverConvs,
  conversationId,
  loadConversation,
  deleteConversation,
  serverLoading,
  refreshServer,
  setOpen,
}: {
  serverConvs: ServerConversation[];
  conversationId: string | null;
  loadConversation: (id: string) => Promise<void>;
  deleteConversation: (id: string) => Promise<void>;
  serverLoading: boolean;
  refreshServer: () => Promise<void>;
  setOpen: (open: boolean) => void;
}) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <History size={13} className="text-violet" />
            <CardTitle>Server archive</CardTitle>
            {serverConvs.length > 0 ? (
              <Badge variant="neutral" size="sm">
                {serverConvs.length}
              </Badge>
            ) : null}
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => void refreshServer()}
            disabled={serverLoading}
          >
            <RefreshCcw
              size={11}
              className={serverLoading ? "animate-spin" : ""}
            />
            Refresh
          </Button>
        </div>
        {serverConvs.length === 0 ? (
          serverLoading ? (
            <PanelSkeleton />
          ) : (
            <CardSub>
              Nothing yet — your next conversation will appear here.
            </CardSub>
          )
        ) : (
          <ul className="m-0 flex max-h-[340px] list-none flex-col gap-1 overflow-y-auto p-0">
            {serverConvs.map((c) => {
              const isCurrent = c.id === conversationId;
              return (
                <li
                  key={c.id}
                  className={cn(
                    "flex items-center gap-2 rounded-md border px-2.5 py-2 transition-colors",
                    isCurrent
                      ? "border-violet/40 bg-aurora-soft"
                      : "border-line bg-surface hover:bg-surface-2",
                  )}
                >
                  <button
                    type="button"
                    onClick={() => {
                      void loadConversation(c.id);
                      setOpen(true);
                    }}
                    className="flex min-w-0 flex-1 items-center gap-2 text-left"
                    title="Load this conversation into the agent drawer"
                  >
                    <ChevronRight size={11} className="shrink-0 text-fg-mute" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[12.5px] font-semibold text-fg">
                        {c.title}
                      </div>
                      <div className="mt-0.5 flex items-center gap-1.5 font-mono text-[10px] text-fg-mute">
                        <span>
                          {new Date(
                            c.lastAt.includes("T")
                              ? c.lastAt
                              : c.lastAt.replace(" ", "T") + "Z",
                          ).toLocaleString()}
                        </span>
                        <span>·</span>
                        <span>{c.messageCount} msg</span>
                        {c.model ? (
                          <>
                            <span>·</span>
                            <span>{c.model}</span>
                          </>
                        ) : null}
                        {isCurrent ? (
                          <Badge variant="info" size="sm">
                            open
                          </Badge>
                        ) : null}
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!confirm(`Delete "${c.title}"? This can't be undone.`))
                        return;
                      void deleteConversation(c.id).then(refreshServer).catch(console.error);
                    }}
                    className="text-fg-mute hover:text-rose"
                    title="Delete this conversation"
                  >
                    <Trash2 size={12} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function KpiTile({ label, value }: { label: string; value: number }) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-0.5 py-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">
          {label}
        </div>
        <div className="text-2xl font-extrabold text-fg">{value}</div>
      </CardContent>
    </Card>
  );
}

function TurnRow({
  userMsg,
  reply,
  onRerun,
}: {
  userMsg: AgentMessage;
  reply: AgentMessage | null;
  onRerun: () => void;
}) {
  if (userMsg.kind !== "user") return null;
  const ts = userMsg.sentAt ? new Date(userMsg.sentAt).toLocaleString() : "";
  return (
    <Card>
      <CardContent className="flex flex-col gap-2.5 py-3">
        {/* Header row — timestamp + outcome chip + rerun button */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 font-mono text-[10.5px] text-fg-mute">
            <span>{ts}</span>
            {reply ? (
              <ReplyBadge reply={reply} />
            ) : (
              <Badge variant="neutral" size="sm">
                no reply
              </Badge>
            )}
          </div>
          <button
            type="button"
            onClick={onRerun}
            title="Re-send this prompt"
            className="inline-flex items-center gap-1 text-[11px] text-violet hover:underline"
          >
            <RotateCcw size={11} />
            Re-send
          </button>
        </div>

        {/* User prompt — chat-bubble style */}
        <div className="self-end max-w-[80%] rounded-2xl rounded-br-md bg-violet/15 px-3 py-2 text-sm text-fg">
          <Send size={11} className="mr-1.5 inline opacity-60" />
          {userMsg.text}
        </div>

        {/* Reply — markdown-rendered for answers, summary for proposals */}
        {reply ? <ReplyBlock reply={reply} /> : null}
      </CardContent>
    </Card>
  );
}

function ReplyBadge({ reply }: { reply: AgentMessage }) {
  if (reply.kind === "answer")
    return (
      <Badge variant="info" size="sm">
        answer
      </Badge>
    );
  if (reply.kind === "error")
    return (
      <Badge variant="err" size="sm">
        error
      </Badge>
    );
  if (reply.kind === "proposal") {
    const status = reply.status;
    if (status === "executed")
      return (
        <Badge variant="ok" size="sm">
          {reply.proposal.tool} · executed
        </Badge>
      );
    if (status === "cancelled")
      return (
        <Badge variant="neutral" size="sm">
          {reply.proposal.tool} · cancelled
        </Badge>
      );
    return (
      <Badge variant="warn" size="sm">
        {reply.proposal.tool} · pending
      </Badge>
    );
  }
  return null;
}

function ReplyBlock({ reply }: { reply: AgentMessage }) {
  if (reply.kind === "answer") {
    return (
      <div className="rounded-2xl rounded-bl-md bg-surface-tint px-3 py-2 text-sm text-fg">
        <MarkdownView text={reply.text} />
      </div>
    );
  }
  if (reply.kind === "error") {
    return (
      <div className="rounded-md border border-rose/30 bg-rose/10 px-3 py-2 text-[12px] text-rose">
        <AlertCircle size={12} className="mr-1 inline" />
        {reply.text}
      </div>
    );
  }
  if (reply.kind === "proposal") {
    return (
      <div className="rounded-xl border border-violet/30 bg-violet/5">
        {reply.proposal.reasoning ? (
          <div className="border-b border-violet/20 bg-violet/10 px-3 py-2 text-[12px] text-fg">
            <MarkdownView text={reply.proposal.reasoning} />
          </div>
        ) : null}
        <div className="px-3 py-2.5">
          <div className="font-mono text-[11px] text-fg-mute">
            <span className="opacity-70">propose</span>
            <span className="mx-1">→</span>
            <span className="font-semibold text-fg">{reply.proposal.tool}</span>
          </div>
          <pre className="mt-2 overflow-auto rounded-md border border-line bg-surface px-2.5 py-1.5 font-mono text-[10.5px] text-fg-dim">
            {JSON.stringify(reply.proposal.queuePayload, null, 2)}
          </pre>
          {reply.status === "executed" && reply.runId ? (
            <div className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-mint">
              <CheckCircle2 size={11} /> Queued as{" "}
              <span className="font-mono">{reply.runId}</span>
            </div>
          ) : null}
        </div>
      </div>
    );
  }
  return null;
}
